import { useEffect, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import { formatInputtingFoodName } from "utils/formater"
import { IMyFood } from "model/food/types/myfood.types"
import { IMyFoodFormModel } from "model/food/types/foodPayload.types"
import {
  IEdamaFoodSearchResult,
  IEdamaIngredientOption,
} from "model/food/types/edama.types"
import { getNutritionFromIngredients } from "features/food/helper/myFoodForm"
import { FoodType, MyFoodEntry } from "model/food/types"

const convertMyFoodToMyFoodFormModel = (
  myFood?: IMyFood,
): IMyFoodFormModel | undefined => {
  if (!myFood) return undefined

  return {
    ...myFood,
    ingredients: myFood.ingredients.map((ingredient) => ({
      ...ingredient,
      uuid: uuidv4(),
    })),
  }
}

const useMyFoodForm = (initFood?: IMyFood) => {
  const [myFood, setMyFood] = useState<IMyFoodFormModel | undefined>(
    convertMyFoodToMyFoodFormModel(initFood),
  )

  useEffect(() => {
    if (initFood) {
      setMyFood(convertMyFoodToMyFoodFormModel(initFood))
    }
  }, [initFood])

  const onNameChange = (name: string) => {
    if (!myFood) return

    const formattedName = formatInputtingFoodName(name)
    setMyFood({ ...myFood, name: formattedName })
  }

  const onPortionChange = (portion: number) => {
    if (!myFood) return
    setMyFood({ ...myFood, portion })
  }

  const onUnitChange = (unit: string) => {
    if (!myFood) return
    setMyFood({ ...myFood, unit })
  }

  const onAddIngredient = ({ id, ...ingredient }: IEdamaFoodSearchResult) => {
    if (!myFood) return
    const ingredientOption: IEdamaIngredientOption = {
      ...ingredient,
      uuid: uuidv4(),
      foodId: id,
    }
    const nextIngredients = [...myFood.ingredients, ingredientOption]

    const nutrition = getNutritionFromIngredients(nextIngredients)

    setMyFood({
      ...myFood,
      ...nutrition,
      ingredients: nextIngredients,
    })
  }

  const onRemoveIngredient = (uuid: string) => {
    if (!myFood) return

    const nextIngredients = myFood.ingredients.filter(
      (ingredient) => ingredient.uuid !== uuid,
    )
    const nutrition = getNutritionFromIngredients(nextIngredients)

    setMyFood({
      ...myFood,
      ...nutrition,
      ingredients: nextIngredients,
    })
  }

  const onUpdateIngredient = (updatedIngredient: IEdamaIngredientOption) => {
    if (!myFood) return

    const nextIngredients = myFood.ingredients.map((ingredient) =>
      updatedIngredient.uuid === ingredient.uuid
        ? updatedIngredient
        : ingredient,
    )
    const nutrition = getNutritionFromIngredients(nextIngredients)

    setMyFood({
      ...myFood,
      ...nutrition,
      ingredients: nextIngredients,
    })
  }

  const onRemoveImage = () => {
    if (!myFood) return
    setMyFood({
      ...myFood,
      imageUrl: undefined,
      imageId: undefined,
      previewImageUrl: undefined,
    })
  }

  return {
    myFood,
    onNameChange,
    onPortionChange,
    onUnitChange,
    onAddIngredient,
    onRemoveIngredient,
    onUpdateIngredient,
    onRemoveImage,
  }
}

export default useMyFoodForm
